import React, { useCallback } from "react";
import classNames from "classnames";
import Button from "./Button";
import styles from "./NumberStepperControl.module.scss";

export type NumberStepperControlProps = {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  helperText?: string;
  inputReadOnly?: boolean;
  decreaseAriaLabel?: string;
  increaseAriaLabel?: string;
};

function clampValue(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

const NumberStepperControl: React.FC<NumberStepperControlProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,
  disabled = false,
  className,
  labelClassName,
  inputClassName,
  helperText,
  inputReadOnly = false,
  decreaseAriaLabel = "Decrease value",
  increaseAriaLabel = "Increase value",
}) => {
  const emitChange = useCallback(
    (next: number) => {
      onChange(clampValue(next, min, max));
    },
    [max, min, onChange]
  );

  const handleInputChange = (raw: string) => {
    const sanitized = raw.replace(/[^0-9]/g, "");
    const normalized = sanitized.replace(/^0+(?=\d)/, "") || "0";

    if (normalized === "0" && sanitized === "") {
      onChange(0);
      return normalized;
    }
    onChange(Math.min(max, Number(normalized)));
    return normalized;
  };

  const handleDecrease = () => {
    const next = value - step;
    emitChange(next < min ? min : next);
  };

  const handleIncrease = () => {
    const next = value + step;
    emitChange(next > max ? max : next);
  };

  const inputId = `number-stepper-${label.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <div className={classNames(styles.wrap, className)}>
      <label htmlFor={inputId} className={classNames(styles.label, labelClassName)}>
        {label}
      </label>

      <div className={styles.controlRow}>
        <input
          id={inputId}
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          className={classNames(styles.input, inputClassName)}
          value={value}
          disabled={disabled}
          readOnly={inputReadOnly}
          onChange={(e) => {
            const normalized = handleInputChange(e.target.value);
            e.currentTarget.value = normalized;
          }}
          onBlur={(e) => {
            const parsed = Number(e.currentTarget.value);
            onChange(Number.isFinite(parsed) ? Math.max(0, Math.min(max, parsed)) : 0);
          }}
        />

        <Button
          type="button"
          disabled={disabled || value <= min}
          variant="white"
          className={styles.stepBtn}
          onClick={handleDecrease}
          aria-label={decreaseAriaLabel}
        >
          -
        </Button>

        <Button
          type="button"
          disabled={disabled || value >= max}
          variant="white"
          className={styles.stepBtn}
          onClick={handleIncrease}
          aria-label={increaseAriaLabel}
        >
          +
        </Button>
      </div>

      {helperText ? <p className={styles.helperText}>{helperText}</p> : null}
    </div>
  );
};

export default NumberStepperControl;
